import React, { useState, useContext, useEffect } from "react";
import { t } from "i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import "yup-phone";
import { PrimaryButton,DefaultButton, TextField } from "@fluentui/react";
import userContext from "../../../context/users/UserContext";
import { submitUserProfileData } from "../../User/helpers/SubmitUserData";
import { setInitialProfileData } from "../../User/helpers/InitialData";
import { userProfile } from "../../../service/UserService";
import MobileInput from "../../../components/MobileInput";
import toastContext from "../../../context/toast/ToastContext";
import loaderContext from "../../../context/loader/LoaderContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ACTIONS,
  AuthRoutes,
  TOAST_TYPE,
  USERTYPES,
} from "../../../constants/Constants";
import TechnicianContact from "../../User/components/TechnicianContact";

const ContactDetailsSchema = Yup.object().shape({
  technicianContactList: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .min(3, "Name must be at least 3 characters.")
        .max(50, "Name must be at most 50 characters.")
        .required("Name cannot be blank."),
    })
  ),
});

const ContactDetails = () => {
  const { roles } = useContext(userContext);
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({});
  const { ref, setLoaderRunning } = useContext(loaderContext);
  // const [errorMessages, setErrorMessages] = useState([]);
  const [isRendered, setIsRendered] = useState(false);
  const initialData = setInitialProfileData(profileData);
  const [customMobileError, setCustomMobileError] = useState(null);
  const [isInitialContactAdded, setIsInitialContactAdded] = useState(false);
  const { showToastv2 } = useContext(toastContext);
  const [showSaveButton, setShowSaveButton] = useState(false);

  const handleInputValidationMobile = (inputNumber, country) => {
    let targetLength = country.format.match(/\./g).length;
    let fixedLength = targetLength - country.dialCode.length;
    let inputTargetLength = inputNumber.length - country.dialCode.length;
    // console.log("--- Mobile Field Values ---",inputNumber,targetLength,fixedLength,inputTargetLength,country);
    if (
      inputTargetLength != fixedLength &&
      inputNumber.length > country.dialCode.length &&
      !(inputNumber.length >= 10 && fixedLength > 10)
    ) {
      console.log("--- Error in validation mobile field ---");
      setCustomMobileError(
        "For " + country.name + " (" + fixedLength + " digits required)"
      );
      return;
    }
    if (inputTargetLength == 0 || inputNumber.length < 2) {
      console.log("---- Error mobile field empty ----");
      setTimeout(() => {
        setCustomMobileError("Mobile cannot be blank");
      }, 100);
      return;
    }
    setShowSaveButton(true);
    console.log("---- No error in mobile field -----");
    setTimeout(() => {
      setCustomMobileError(null);
    }, 100);
  };

  const getUserProfile = async () => {
    let response = await userProfile();
    if (response.status === 200 && response.data.result === "SUCCESS") {
      setProfileData(response.data.data);
      console.log("Profile data in ContactDetails.jsx: ", profileData);
      setValues({
        ...response.data.data,
        technicianContactList: response.data.data.technicianContactList,
      });
      if( response.data.data.technicianContactList?.length == 0){
        setIsRendered(true);
        setShowSaveButton(false);
      } 
    }
  };

  useEffect(() => {
    getUserProfile();
  }, []);

  const {
    values,
    errors,
    setFieldValue,
    setFieldError,
    validateForm,
    handleChange,
    handleSubmit,
    touched,
    handleBlur,
    setValues,
    resetForm
  } = useFormik({
    initialValues: {
      ...initialData,
      technicianContactList: [],
    },
    validationSchema: ContactDetailsSchema,
    onSubmit: async (values, actions) => {

      setLoaderRunning(true);
      ref.current.continuousStart();
      //setIsRendered(!isRendered);

      const profileWithTechnicianContactList = {
        ...values,
        technicianContactList: [
          ...values.technicianContactList.map((contact) => ({
            id : contact.id,
            userId : contact.userId,
            name: contact.name,
            mobile: contact.mobile,
            isPrimaryContact: contact.isPrimaryContact,
          })),
        ],
      };

      try {
        const response = await submitUserProfileData(
          roles[0],
          profileWithTechnicianContactList,
          profileData
        );
        if (response.status === 200 && response.data.result === "SUCCESS") {
          if(values.technicianContactList.length == 0){
            setIsRendered(true);
            setShowSaveButton(false);
          }else{
            setIsRendered(false);
          }
          setLoaderRunning(false);
          ref.current.complete();
          showToastv2(TOAST_TYPE.SUCCESS, "User updated successfully");
        }
        else if (response.status === 400) {
          setLoaderRunning(false);
          ref.current.complete();
          showToastv2(TOAST_TYPE.ERROR, response?.data?.message);
          return;
        }
      } catch (error) {
        console.error(
          "An error occurred while submitting the profile data",
          error
        );
        setLoaderRunning(false);
        ref.current.complete();
      }
    },
  });


  const addTechnicianContact = async () => {
    const isValid = await validateForm();
    if (Object.keys(isValid).length === 0) {
      // setErrorMessages([]);
      setFieldValue("technicianContactList", [
        ...values.technicianContactList,
        { name: "", mobile: "" },
      ]);
    }
    if(values?.technicianContactList.length == 0 && showSaveButton == true){
      getUserProfile();
    }
  };

  const removeTechnicianContact = (index) => {
    const updatedList = values.technicianContactList.filter((_, i) => i !== index);
    setFieldValue("technicianContactList", updatedList);
    setShowSaveButton(true);
  };

  const handleTechnicianContactChange = (index, field, value) => {
    const updatedList = values?.technicianContactList.map((contact, i) => {
      if (field === 'isPrimaryContact') {
        return i === index ? { ...contact, [field]: value } : { ...contact, [field]: 0 };
      }
      return i === index ? { ...contact, [field]: value } : contact;
    });
    setShowSaveButton(true);
    setFieldValue("technicianContactList", updatedList);
  };

  const getTextHeader = ()=>{
    if(values?.technicianContactList.length > 0){
      if(showSaveButton != true) setShowSaveButton(true);
      return "Add more contacts";
    }else{
      return "Edit";
    }
  }

  return (
    <>
      <div className="tab-section">
        <section className="sectionA">
          <p
            style={{ fontSize: "20px", fontWeight: "600", padding: "0px 0px" }}
          >
            {t("account-and-settings.contact-details")}
          </p>
        </section>

        {!isRendered && (
          <div className="ms-Grid" dir="ltr">
            {values.technicianContactList.map((contact, index) => (
              <div
                className="ms-Grid-row"
                key={index}
                style={{ marginTop: "20px" }}
              >
                <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3 ms-xl4">
                  <div className="form-heading">{t("user.name")}</div>
                  <div className="form-mt-10">{contact.name}</div>
                </div>
                <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3 ms-xl4">
                  <div className="form-heading">{t("user.mobile")}</div>
                  <div className="form-mt-10">{contact.mobile}</div>
                </div>
                {contact.isPrimaryContact == 1 && (
                  <div className="ms-Grid-col ms-sm12 ms-md6 ms-lg3 ms-xl4">
                    <div className="form-mt-10">Primary Contact</div>
                  </div>
                )}
              </div>
            ))}

            <div className="view-page-buttons">
              <PrimaryButton
                className="view-page-button"
                text="Edit"
                onClick={() => setIsRendered(!isRendered)}
              />

              <DefaultButton
                className="view-page-button"
                text="Back"
                onClick={() => navigate(-1)}
              />
            </div>
          </div>
        )}

        {isRendered && (
          <div className="ms-Grid" dir="ltr">
            {values.technicianContactList.map((contact, index) => (
              <TechnicianContact
                key={index}
                index={index}
                contact={contact}
                handleTechnicianContactChange={handleTechnicianContactChange}
                handleBlur={handleBlur}
                touched={touched}
                errors={errors}
                handleInputValidationMobile={handleInputValidationMobile}
                removeTechnicianContact={removeTechnicianContact}
              />
            ))}
             {values.technicianContactList.length === 0 && (
                  <div style={{fontStyle : "italic", marginTop : "10px"}}>No additional contacts available</div>
              )}
            <div className="view-page-buttons" style={{marginTop : "27px"}}>
              {showSaveButton && (
                <PrimaryButton
                  className="view-page-button"
                  text={t("button.save")}
                  onClick={handleSubmit}
                  disabled={
                    Object.keys(errors).length > 0 || // Disable if there are any validation errors
                    values.technicianContactList.some(
                      (contact) =>
                        !contact.name ||
                        !contact.mobile ||
                        customMobileError != null // Disable if any name or mobile is empty
                    )
                  }
                />
              )}
             
              <PrimaryButton
                className="view-page-button"
                text={getTextHeader()}
                onClick={addTechnicianContact}
                disabled={
                  Object.keys(errors).length > 0 || // Disable if there are any validation errors
                  values.technicianContactList.some(
                    (contact) =>
                      !contact.name ||
                      !contact.mobile ||
                      customMobileError != null // Disable if any name or mobile is empty
                  )
                }
              />
              {values.technicianContactList.length > 0 && (
                <DefaultButton
                  className="view-page-button"
                  text="Cancel"
                  onClick={() => {
                    setIsRendered(false);
                    getUserProfile();
                    // resetForm();
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ContactDetails;